import React, { useEffect, useMemo, useRef, useState } from "react";
import Spacing from "../Spacing";
import { Drawer } from "vaul";
import { X } from "lucide-react";
import { notify } from "../../helpers";

export default function ErrorLogsViewModal(props) {
  const {
    logData = {},
    activeModal = false,
    setActiveModal = () => {},
    previousScrollPosition = 0,
  } = props;

  const dropdownRef = useRef(null);

  const logs = useMemo(() => {
    if (Array.isArray(logData)) return logData.filter(Boolean);
    if (logData && typeof logData === "object" && Object.keys(logData).length) return [logData];
    return [];
  }, [logData]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  // “Live”: if new logs come, keep selection if valid; else latest
  useEffect(() => {
    if (!logs.length) return;
    setSelectedIndex((prev) => (prev >= 0 && prev < logs.length ? prev : logs.length - 1));
  }, [logs.length]);

  const formatText = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

  const prettyText = (obj) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  function inferSeverity(d) {
    // same heuristic as your html sample (you can refine)
    const cat = String(d?.category || "").toLowerCase();
    const msg = String(d?.error?.message || "").toLowerCase();
    if (cat.includes("server_status") || msg.includes("error") || msg.includes("issues")) return "bad";
    if (cat.includes("warn")) return "warn";
    return "ok";
  }

  function sevLabel(sev) {
    if (sev === "bad") return "Error";
    if (sev === "warn") return "Warning";
    return "OK";
  }

  async function copyText(text) {
    const t = String(text ?? "");
    try {
      await navigator.clipboard.writeText(t);
      return notify('success', 'Copied successfully', 2);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = t;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        return notify('success', 'Copied successfully', 2);
      } catch {
        return notify('error', 'Copied failed', 2);
      }
    }
  }

  const selectedLog = logs[selectedIndex] || null;

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  // data mapping (same as html)
  const sev = selectedLog ? inferSeverity(selectedLog) : "ok";

  const title = formatText(selectedLog?.error?.message || selectedLog?.category || "Event");
  const feature = formatText(selectedLog?.feature);
  const category = formatText(selectedLog?.category);
  const id = formatText(selectedLog?.id);
  const timestamp = formatText(selectedLog?.timestamp);
  const createdAt = formatText(selectedLog?.created_at);
  const updatedAt = formatText(selectedLog?.updated_at);

  const url = formatText(selectedLog?.context?.url);
  const userAgent = formatText(selectedLog?.context?.userAgent);
  const method = formatText(selectedLog?.context?.method);
  const statusCode = formatText(selectedLog?.context?.statusCode);
  const endpoint = formatText(selectedLog?.context?.endpoint);

  const errName = formatText(selectedLog?.error?.name);
  const errType = formatText(selectedLog?.error?.type);
  const stack = formatText(selectedLog?.error?.stack);

  const rawJson = selectedLog ? prettyText(selectedLog) : "";

  const dotColor = sev === "bad" ? "var(--dp-bad)" : sev === "warn" ? "var(--dp-warn)" : "var(--dp-ok)";

  console.log(errName);
    

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose} dismissible={false}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn" />
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay" />
          <Drawer.Content
            className="docpro_drawer_content docpro_drawer_style_4"
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <Drawer.Title className="sr-only">View Logs</Drawer.Title>
            <Drawer.Description className="sr-only">View Log Details</Drawer.Description>

            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">
              <div className="docpro_modal">
                <button className="docpro_close_btn" onClick={handleDrawerClose}>
                  <X />
                </button>

                <h2 className="docpro_fs_24 docpro_mb_0 text-center">Error Log View</h2>
                <Spacing lg="10" sm="25" />

                {/* =========================
                    SAME DESIGN MOUNT (scoped)
                   ========================= */}
                <div id="docpro-log-view">
                  {/* Inline-scoped CSS: copied from your log_view.html */}
                  <style>{`
                    #docpro-log-view {
                      --dp-bg: #f6f9fc;
                      --dp-text: #0f172a;
                      --dp-muted: #64748b;
                      --dp-border: rgba(15, 23, 42, 0.10);
                      --dp-card: rgba(255, 255, 255, 0.70);
                      --dp-card-2: rgba(255, 255, 255, 0.82);
                      --dp-shadow: 0 18px 45px rgba(15, 23, 42, 0.10);
                      --dp-primary: #0ea5e9;
                      --dp-ok: #10b981;
                      --dp-warn: #f59e0b;
                      --dp-bad: #ef4444;
                      --dp-radius: 18px;
                      --dp-radius-sm: 14px;
                      --dp-mono: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
                      --dp-sans: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
                      font-family: var(--dp-sans);
                      color: var(--dp-text);
                    }

                    #docpro-log-view * { box-sizing: border-box; }

                    #docpro-log-view .dp-card {
                      background: var(--dp-card);
                      border: 1px solid var(--dp-border);
                      border-radius: var(--dp-radius);
                      box-shadow: var(--dp-shadow);
                      backdrop-filter: blur(14px);
                      -webkit-backdrop-filter: blur(14px);
                      padding: 20px;
                    }

                    #docpro-log-view .dp-header {
                      display: flex;
                      gap: 10px;
                      align-items: flex-start;
                      justify-content: space-between;
                      flex-wrap: wrap;
                    }

                    #docpro-log-view .dp-title {
                      display: flex;
                      flex-direction: column;
                      gap: 6px;
                      min-width: 260px;
                    }

                    #docpro-log-view .dp-title h1 {
                      margin: 0;
                      font-size: 20px;
                      line-height: 1.2;
                      letter-spacing: 0.2px;
                    }

                    #docpro-log-view .dp-subtitle {
                      display: flex;
                      flex-wrap: wrap;
                      gap: 8px;
                      align-items: center;
                      color: var(--dp-muted);
                      font-size: 13px;
                    }

                    #docpro-log-view .dp-pill {
                      display: inline-flex;
                      align-items: center;
                      gap: 8px;
                      padding: 4px 10px;
                      border-radius: 999px;
                      border: 1px solid var(--dp-border);
                      background: rgba(255,255,255,0.55);
                      font-size: 12px;
                      color: var(--dp-text);
                      white-space: nowrap;
                    }

                    #docpro-log-view .dp-dot {
                      width: 9px;
                      height: 9px;
                      border-radius: 50%;
                      display: inline-block;
                    }

                    #docpro-log-view .dp-actions {
                      display: flex;
                      gap: 10px;
                      align-items: center;
                      flex-wrap: wrap;
                      justify-content: flex-end;
                    }

                    #docpro-log-view .dp-btn {
                      border: 1px solid var(--dp-border);
                      background: rgba(255,255,255,0.7);
                      color: var(--dp-text);
                      padding: 8px 10px;
                      border-radius: 12px;
                      font-size: 12px;
                      cursor: pointer;
                      transition: transform .08s ease, background .15s ease;
                      user-select: none;
                    }
                    #docpro-log-view .dp-btn:hover { background: rgba(255,255,255,0.92); }
                    #docpro-log-view .dp-btn:active { transform: translateY(1px); }

                    #docpro-log-view .dp-toast {
                      font-size: 12px;
                      color: var(--dp-muted);
                      min-height: 16px;
                      margin-top: 8px;
                    }

                    #docpro-log-view .dp-grid {
                      display: grid;
                      grid-template-columns: repeat(12, 1fr);
                      gap: 14px;
                      margin-top: 16px;
                    }

                    #docpro-log-view .dp-field {
                      grid-column: span 6;
                      background: var(--dp-card-2);
                      border: 1px solid var(--dp-border);
                      border-radius: var(--dp-radius-sm);
                      padding: 14px;
                      min-height: 72px;
                    }
                    #docpro-log-view .dp-field.dp-wide { grid-column: span 12; }

                    #docpro-log-view .dp-k {
                      font-size: 11px;
                      color: var(--dp-muted);
                      text-transform: uppercase;
                      letter-spacing: 0.09em;
                    }

                    #docpro-log-view .dp-v {
                      margin-top: 7px;
                      font-size: 14px;
                      line-height: 1.35;
                      word-break: break-word;
                    }

                    #docpro-log-view a.dp-link {
                      color: var(--dp-primary);
                      text-decoration: none;
                    }
                    #docpro-log-view a.dp-link:hover { text-decoration: underline; }

                    #docpro-log-view .dp-row {
                      display: flex;
                      gap: 10px;
                      align-items: center;
                      justify-content: space-between;
                      flex-wrap: wrap;
                    }

                    #docpro-log-view details.dp-details {
                      background: rgba(255,255,255,0.72);
                      border-radius: var(--dp-radius-sm);
                    }

                    #docpro-log-view details.dp-details summary {
                      cursor: pointer;
                      font-weight: 700;
                      font-size: 13px;
                      color: var(--dp-text);
                      list-style: none;
                      outline: none;
                    }
                    #docpro-log-view details.dp-details summary::-webkit-details-marker { display: none; }

                    #docpro-log-view .dp-pre {
                      margin: 10px 0 0;
                      padding: 12px;
                      border-radius: 12px;
                      border: 1px solid var(--dp-border);
                      background: rgba(15,23,42,0.92);
                      color: #e5e7eb;
                      font-family: var(--dp-mono);
                      font-size: 12px;
                      line-height: 1.5;
                      overflow: auto;
                      white-space: pre;
                    }

                    #docpro-log-view .dp-muted { color: var(--dp-muted); }
                    #docpro-log-view .dp-mini { font-size: 12px; color: var(--dp-muted); }

                    @media (max-width: 780px) {
                      #docpro-log-view .dp-field { grid-column: span 12; }
                      #docpro-log-view .dp-card { padding: 12px; }
                      #docpro-log-view .dp-field { padding: 10px; }
                      #docpro-log-view .dp-grid { gap: 8px; }
                    }

                    #docpro-log-view .dp-details-head{
                      display:flex;
                      align-items:center;
                      justify-content:space-between;
                      gap:10px;
                      padding: 2px 2px 8px 2px;
                      font-weight: 700;
                      font-size: 13px;
                      color: var(--dp-text);
                    }

                    #docpro-log-view .dp-mini-btn{
                      padding: 6px 10px;
                      border-radius: 10px;
                      font-size: 12px;
                    }

                    #docpro-log-view .dp-pre.dp-pre-mini{
                      margin: 0;
                      padding: 10px;
                      border-radius: 12px;
                      border: 1px solid var(--dp-border);
                      background: rgba(15,23,42,0.92);
                      color: #e5e7eb;
                      font-family: var(--dp-mono);
                      font-size: 11.5px;
                      line-height: 1.45;
                      overflow: auto;
                      white-space: pre;
                    }
                  `}</style>

                  <div className="dp-card">
                    {!selectedLog ? (
                      <div style={{ fontFamily: "var(--dp-mono)" }}>No logs available.</div>
                    ) : (
                      <>
                        {/* Header */}
                        <div className="dp-header">
                          <div className="dp-title">
                            <h1>{title}</h1>
                            <div className="dp-subtitle">
                              <span className="dp-pill">
                                Feature: <strong>{feature}</strong>
                              </span>
                              <span className="dp-pill">
                                ID: <strong>#{id}</strong>
                              </span>
                              <span className="dp-pill">
                                Category: <strong>{category}</strong>
                              </span>
                            </div>
                          </div>

                          <div className="dp-actions">
                            <span className="dp-pill" title="Severity">
                              <span className="dp-dot" style={{ background: dotColor }} />
                              {sevLabel(sev)}
                            </span>
                            <span className="dp-pill" title="Timestamp">
                              {timestamp}
                            </span>
                            <span className="dp-pill" title="Extension Version">
                              Ext: <strong>{formatText(selectedLog?.environment?.extensionVersion)}</strong>
                            </span>

                            <button
                              className="dp-btn"
                              onClick={async () => {
                                await copyText(rawJson);
                              }}
                            >
                              Copy JSON
                            </button>

                            <button
                              className="dp-btn"
                              onClick={async () => {
                                await copyText(stack);
                              }}
                            >
                              Copy Stack
                            </button>
                          </div>
                        </div>

                        {/* Grid */}
                        <div className="dp-grid">
                          <div className="dp-field">
                            <div className="dp-row">
                              <div>
                                <div className="dp-k">URL</div>
                                <div className="dp-v">
                                  {url === "—" ? (
                                    "—"
                                  ) : (
                                    <a
                                      className="dp-link"
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {url}
                                    </a>
                                  )}
                                </div>
                              </div>

                              <button
                                className="dp-btn"
                                onClick={async () => {
                                  await copyText(url);
                                }}
                              >
                                Copy URL
                              </button>
                            </div>
                          </div>

                          <div className="dp-field">
                            <div className="dp-k">User Agent</div>
                            <div className="dp-v">{userAgent}</div>
                          </div>

                          <div className="dp-field">
                            <div className="dp-k">Method</div>
                            <div className="dp-v">{method}</div>
                          </div>

                          <div className="dp-field">
                            <div className="dp-k">Status Code</div>
                            <div className="dp-v">{statusCode}</div>
                          </div>

                          <div className="dp-field">
                            <div className="dp-k">Endpoint</div>
                            <div className="dp-v">{endpoint}</div>
                          </div>

                          <div className="dp-field">
                            <div className="dp-k">Timestamps</div>
                            <div className="dp-v">
                              <div>
                                <span className="dp-mini">Created:</span> {createdAt}
                              </div>
                              <div>
                                <span className="dp-mini">Updated:</span> {updatedAt}
                              </div>
                            </div>
                          </div>

                          <div className="dp-field dp-wide">
                            <div className="dp-k">Error</div>
                            <div className="dp-v">
                              <div className="mb-1"><strong>Message: </strong><span className="dp-mini">{title}</span></div>
                              <strong>{errName}{": "}</strong>
                              <span className="dp-muted">({errType})</span>
                            </div>

                            <div className="dp-details dp-static">
                              <div className="dp-details-head">
                                <span>Stack Trace</span>
                                <button
                                  className="dp-btn dp-mini-btn"
                                  onClick={async () => {
                                    await copyText(stack);
                                  }}
                                >
                                  Copy
                                </button>
                              </div>

                              <pre className="dp-pre dp-pre-mini">{stack}</pre>
                            </div>
                          </div>

                          <div className="dp-field dp-wide">
                            <div className="dp-details dp-static">
                              <div className="dp-details-head">
                                <span>Raw Response (JSON)</span>
                                <button
                                  className="dp-btn dp-mini-btn"
                                  onClick={async () => {
                                    await copyText(rawJson);
                                  }}
                                >
                                  Copy
                                </button>
                              </div>

                              <pre className="dp-pre dp-pre-mini">{rawJson}</pre>
                            </div>
                          </div>
                        </div>

                        {/* Optional: if you pass array logs, show a small picker row */}
                        {logs.length > 1 && (
                          <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {logs.map((l, i) => (
                              <button
                                key={i}
                                className="dp-btn"
                                onClick={() => setSelectedIndex(i)}
                                style={{
                                  background: i === selectedIndex ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.7)",
                                  fontWeight: i === selectedIndex ? 700 : 500,
                                }}
                                title={formatText(l?.timestamp || l?.created_at || "")}
                              >
                                #{formatText(l?.id || i + 1)}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
